import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient } from "@charphq/api-client";
import { AuthProvider } from "@charphq/ui/providers/auth-provider";
import { GlobalErrorBoundary } from "@charphq/ui/components/error";
import { ErrorProvider } from "@charphq/ui/providers";
import { ToastProvider, ToastViewport } from "@charphq/ui/components/base/toast";
import { Toaster } from "@charphq/ui/components/base/toaster";
// Sonner renderer — several pages (support triage, deployments, customers, …)
// fire `toast` from "sonner"; without this mount those toasts are silently
// dropped (OTO-96).
import { Toaster as SonnerToaster } from "@charphq/ui/components/base/sonner";
import { WebRouterProvider } from "./lib/router";
import { PostHogWrapper } from "./lib/posthog-provider";
import "./index.css";

// Import API/auth client initialization functions
import { initializeApiClient } from "./lib/api-client";
import { initializeAuthClient, authClient } from "./lib/auth-client";

// Create a client using shared configuration from @charphq/api-client
const queryClient = createQueryClient();

// Render function to avoid duplication
function renderApp() {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <PostHogWrapper>
        <GlobalErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <ErrorProvider>
              <ToastProvider>
                <AuthProvider authClient={authClient}>
                  <WebRouterProvider />
                </AuthProvider>
                <ToastViewport />
                <Toaster />
                <SonnerToaster />
              </ToastProvider>
            </ErrorProvider>
          </QueryClientProvider>
        </GlobalErrorBoundary>
      </PostHogWrapper>
    </React.StrictMode>,
  );
}

// Initialize API and auth clients before rendering React
// This ensures runtime environment variables are loaded before any API/auth calls are made
async function bootstrap() {
  try {
    await Promise.all([initializeApiClient(), initializeAuthClient()]);
  } catch (error) {
    // If initialization fails, still render the app but log the error
    console.error("Failed to initialize clients:", error);
  } finally {
    renderApp();
  }
}

void bootstrap();
